import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, fullName: string, cpfCnpj: string, captchaToken: string, phone?: string, companyName?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string, captchaToken: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    console.log("🔐 AuthContext: Initializing auth state...");
    
    // Security timeout - ensure loading never stays true forever
    const loadingTimeout = setTimeout(() => {
      console.warn("⚠️ AuthContext: Loading timeout reached, forcing loading to false");
      setLoading(false);
    }, 5000);

    // Set up auth state listener FIRST
    console.log("🔐 AuthContext: Setting up onAuthStateChange listener...");
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("🔐 AuthContext: Auth state changed", { event, hasSession: !!session });
        clearTimeout(loadingTimeout);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    console.log("🔐 AuthContext: Checking for existing session...");
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error("❌ AuthContext: Error getting session", error);
      } else {
        console.log("🔐 AuthContext: Got session", { hasSession: !!session });
      }
      clearTimeout(loadingTimeout);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch((error) => {
      console.error("❌ AuthContext: Exception getting session", error);
      clearTimeout(loadingTimeout);
      setLoading(false);
    });

    return () => {
      console.log("🔐 AuthContext: Cleaning up...");
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    cpfCnpj: string,
    captchaToken: string,
    phone?: string,
    companyName?: string
  ) => {
    try {
      console.log("🔐 AuthContext.signUp: Prosseguindo com cadastro...");
      
      const redirectUrl = `${window.location.origin}/area-cliente`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            cpf_cnpj: cpfCnpj,
            phone: phone || "",
            company_name: companyName || "",
          },
        },
      });

      if (error) {
        toast({
          title: "Erro ao cadastrar",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      // If signup successful, sync with Asaas
      if (data.user) {
        toast({
          title: "Cadastro realizado!",
          description: "Sincronizando suas informações...",
        });

        // Call sync function after a short delay to ensure profile is created
        setTimeout(async () => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            
            if (session) {
              const { data: syncResult, error: syncError } = await supabase.functions.invoke(
                "sync-asaas-customer",
                {
                  headers: {
                    Authorization: `Bearer ${session.access_token}`,
                  },
                }
              );
              
              if (!syncError) {
                console.log("Asaas customer synced successfully:", syncResult);
                
                // Invalidar caches para atualizar UI automaticamente
                await queryClient.invalidateQueries({ queryKey: ["profile"] });
                await queryClient.invalidateQueries({ queryKey: ["subscription"] });
                await queryClient.invalidateQueries({ queryKey: ["payments"] });
                
                if (syncResult?.syncedSubscriptions > 0) {
                  toast({
                    title: "Assinaturas sincronizadas!",
                    description: `${syncResult.syncedSubscriptions} assinatura(s) encontrada(s).`,
                  });
                }
              }
            }
          } catch (syncError) {
            console.error("Error syncing Asaas customer:", syncError);
          }
        }, 2000);
      }

      return { error };
    } catch (error: any) {
      toast({
        title: "Erro inesperado",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  const signIn = async (email: string, password: string, captchaToken: string) => {
    try {
      console.log("🔐 AuthContext.signIn: Prosseguindo com login...");
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error("❌ AuthContext.signIn: Erro ao fazer login", error);
        toast({
          variant: "destructive",
          title: "Erro ao fazer login",
          description: error.message,
        });
        return { error };
      }

      console.log("✅ AuthContext.signIn: Login bem-sucedido");

      // Verificar se o usuário tem role de admin
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', data.user.id)
        .eq('role', 'admin')
        .single();

      if (error) {
        toast({
          title: "Erro ao fazer login",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      // Após login bem-sucedido, verificar se precisa sincronizar
      if (data.session) {
        // Buscar perfil do usuário
        const { data: profile } = await supabase
          .from("profiles")
          .select("asaas_customer_id, cpf_cnpj")
          .eq("id", data.user.id)
          .single();

        // Se não tem asaas_customer_id OU não tem cpf_cnpj, fazer sync
        if (profile && (!profile.asaas_customer_id || !profile.cpf_cnpj)) {
          console.log("Profile needs sync, calling sync-asaas-customer...");
          
          // Mostrar toast informativo
          toast({
            title: "Sincronizando...",
            description: "Buscando suas assinaturas do Asaas...",
          });

          // Chamar sync em background
          setTimeout(async () => {
            try {
              const { data: syncResult, error: syncError } = await supabase.functions.invoke(
                "sync-asaas-customer",
                {
                  headers: {
                    Authorization: `Bearer ${data.session.access_token}`,
                  },
                }
              );

              if (syncError) {
                console.error("Error syncing:", syncError);
              } else {
                console.log("Sync result:", syncResult);
                
                // Invalidar caches para atualizar UI automaticamente
                await queryClient.invalidateQueries({ queryKey: ["profile"] });
                await queryClient.invalidateQueries({ queryKey: ["subscription"] });
                await queryClient.invalidateQueries({ queryKey: ["payments"] });
                
                if (syncResult?.syncedSubscriptions > 0) {
                  toast({
                    title: "Assinaturas sincronizadas!",
                    description: `${syncResult.syncedSubscriptions} assinatura(s).`,
                  });
                }
              }
            } catch (syncError) {
              console.error("Error calling sync function:", syncError);
            }
          }, 1000);
        }
      }

      return { error };
    } catch (error: any) {
      toast({
        title: "Erro inesperado",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logout realizado",
      description: "Você foi desconectado com sucesso.",
    });
  };

  return (
    <AuthContext.Provider value={{ user, session, signUp, signIn, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
